import { useEffect, useMemo, useRef, useState, type FunctionComponent } from "react";
import { MessageOutlined, NotificationOutlined } from "@ant-design/icons";
import { useVisibilityChange } from "@uidotdev/usehooks";
import { Button, Empty, Input, List, Segmented, Space, Spin } from "antd";
import { useTranslation } from "react-i18next";

import { chatActions, RequestStatus } from "@fitness-app/app-store";
import { PermissionKey } from "@fitness-app/data-models";
import { ChatStatus, ChatType, type ChatChannelWithLastMessage } from "@fitness-app/data-models/entities/Chat";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { usePermission, usePermissionScope } from "~/hooks/usePermission";
import ChatChannelItem from "~/modules/Chat/ChatChannelItem";
import ChatTrainerFilterDropdown, { OWN_CHATS_MENU_KEY } from "~/modules/Chat/ChatTrainerFilterDropdown";
import { canFilterByTrainer } from "~/modules/Chat/chatTrainerPermission";
import { sortChannelsByUrgency, useChannelResponseMetrics } from "~/modules/Chat/hooks/useChannelResponseMetrics";
import { useChatTeamMemberDirectory } from "~/modules/Chat/hooks/useChatTeamMemberDirectory";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  chatStatus: ChatStatus;
  changeTab: (status: ChatStatus) => void;
  status: {
    status: RequestStatus | null;
    fetchingInitialPage: boolean;
    fetchingNextPage: boolean;
    isLastPage: boolean;
    fetchingExtraChannel: boolean;
  };
  channels: ChatChannelWithLastMessage[];
  sortByResponseTime?: boolean;
}

type Props = OwnProps;

const ChatChannelsList: FunctionComponent<Props> = ({
  chatStatus,
  changeTab,
  channels,
  status,
  sortByResponseTime,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["chat", "dashboard"]);
  const documentVisible = useVisibilityChange();
  const subscriptionStatus = useAppSelector((store) => store.chat.status);
  const prevSubscriptionStatus = useRef<RequestStatus | null>(null);
  const { userId } = useUserRole();
  // Kanał ogłoszeniowy jest per klientka, nie tenantowy: siedzi w `assignedUsers` razem z jej
  // trenerką, a lista i tak filtruje po widzu. Gate na `chat.read = ALL` chował przełącznik przed
  // każdą trenerką (wszystkie mają ASSIGNED), więc widziała ogłoszenia tylko head coach — a to
  // właśnie trenerka ma wiedzieć, co poszło do jej klientek.
  const canSeeAnnouncementChannels = usePermission(PermissionKey.CHAT_READ);
  const chatReadScope = usePermissionScope(PermissionKey.CHAT_READ);
  const canUseTrainerFilter = canFilterByTrainer(chatReadScope);
  const { memberId } = useUserClaims();
  // Announcement channels stay out of the default view on purpose — they are broadcast
  // containers, not conversations, and interleaving them doubled every client's rows.
  const [channelTypePreset, setChannelTypePreset] = useState<"default" | "marketing">("default");
  const [selectedFilterKey, setSelectedFilterKey] = useState<string>(OWN_CHATS_MENU_KEY);
  const [searchValue, setSearchValue] = useState<string>();
  const { data: trainers = [] } = useChatTeamMemberDirectory({
    enabled: canUseTrainerFilter,
    permissionScope: chatReadScope,
  });

  const trainerUserId = useMemo(() => {
    if (selectedFilterKey === OWN_CHATS_MENU_KEY) return userId || undefined;
    const trainer = trainers.find((member) => member.id === selectedFilterKey);
    return trainer ? (trainer.userId ?? trainer.id) : userId || undefined;
  }, [selectedFilterKey, trainers, userId]);

  const fetchListParams = useMemo(() => {
    const base = {
      status: chatStatus,
      ...(searchValue?.trim() ? { searchValue: searchValue.trim() } : {}),
      ...(trainerUserId ? { trainerUserId } : {}),
      type:
        channelTypePreset === "marketing"
          ? ([ChatType.OneWayMarketing, ChatType.TwoWayMarketing] as ChatType[])
          : ([ChatType.OneWay, ChatType.TwoWay] as ChatType[]),
    };
    return base;
  }, [chatStatus, searchValue, trainerUserId, channelTypePreset]);

  const fetchListParamsRef = useRef(fetchListParams);

  useEffect(() => {
    fetchListParamsRef.current = fetchListParams;
  }, [fetchListParams]);

  const channelIds = useMemo(() => channels.map((c) => c.id), [channels]);
  const { data: responseMetrics } = useChannelResponseMetrics(channelIds);

  const sortedChannels = useMemo(() => {
    if (!sortByResponseTime || !responseMetrics || chatStatus === ChatStatus.Archived) return channels;
    return sortChannelsByUrgency(channels, responseMetrics);
  }, [channels, responseMetrics, chatStatus, sortByResponseTime]);

  useEffect(() => {
    void dispatch(chatActions.fetchInitialChatListPage(fetchListParams));
  }, [dispatch, fetchListParams]);

  useEffect(() => {
    if (
      documentVisible &&
      subscriptionStatus === RequestStatus.SUBSCRIBED &&
      prevSubscriptionStatus.current === RequestStatus.SUBSCRIBED
    ) {
      void dispatch(chatActions.fetchInitialChatListPage(fetchListParamsRef.current));
    }

    prevSubscriptionStatus.current = subscriptionStatus;
  }, [documentVisible, subscriptionStatus, dispatch]);

  const renderLoadMoreButton = () => {
    if (!status.fetchingInitialPage && !status.fetchingNextPage && !status.isLastPage) {
      return (
        <div className="my-4 h-12 text-center">
          <Button
            size="small"
            onClick={() => void dispatch(chatActions.getNextChatListPage(fetchListParamsRef.current))}
          >
            pobierz więcej
          </Button>
        </div>
      );
    }
    if (status.fetchingNextPage) {
      return (
        <div className="my-4 h-12 text-center">
          <Spin size="small" />
        </div>
      );
    }

    return null;
  };

  const onSearch = (raw: string) => {
    setSearchValue(raw.trim() ? raw : undefined);
  };

  return (
    <>
      {canSeeAnnouncementChannels && (
        <div className="mb-3 px-3">
          <Segmented
            block
            value={channelTypePreset}
            onChange={(value) => setChannelTypePreset(value as "default" | "marketing")}
            options={[
              {
                label: (
                  <Space size={6}>
                    <MessageOutlined />
                    Czaty
                  </Space>
                ),
                value: "default",
              },
              {
                label: (
                  <Space size={6}>
                    <NotificationOutlined />
                    Ogłoszeniowe
                  </Space>
                ),
                value: "marketing",
              },
            ]}
          />
        </div>
      )}

      <div className="flex w-full shrink-0 items-center gap-2 px-[10px] pb-5">
        <Input.Search
          placeholder="Wyszukaj przez imię, nazwisko lub email"
          onSearch={onSearch}
          allowClear
          className="min-w-0 flex-1"
        />
        {canUseTrainerFilter && trainers.length > 1 ? (
          <ChatTrainerFilterDropdown
            trainers={trainers}
            userId={userId}
            memberId={memberId}
            selectedFilterKey={selectedFilterKey}
            onSelectionChange={setSelectedFilterKey}
          />
        ) : null}
      </div>

      <List
        loadMore={renderLoadMoreButton()}
        itemLayout="horizontal"
        dataSource={sortedChannels}
        loading={status.fetchingInitialPage && subscriptionStatus !== RequestStatus.SUBSCRIBED}
        renderItem={(channel) => (
          <ChatChannelItem
            channel={channel}
            changeTab={changeTab}
            archived={chatStatus === ChatStatus.Archived}
            responseMetric={chatStatus !== ChatStatus.Archived ? responseMetrics?.[channel.id] : undefined}
          />
        )}
        locale={{
          emptyText: <Empty className="mt-12" description={t("dashboard:chatDrawer.emptyChannelList")} />,
        }}
        style={{ minHeight: 500 }}
        className="max-h-full overflow-y-auto px-4"
      />
    </>
  );
};

export default ChatChannelsList;
